import React, { useState, useEffect, useRef } from 'react';
import { sanitizeSermonHtml } from '../lib/sanitize';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Book, Image, Quote, Target, Star, Download, Printer, MoreVertical, MinusCircle, PlusCircle, Link as LinkIcon, Clock, AlignLeft, AlignRight, FileText, FileDown as FileWord, BookOpen } from 'lucide-react';
import ReactPlayer from 'react-player/youtube';
import ReactMarkdown from 'react-markdown';
import { MediaDownloader } from './MediaDownloader';
import { useSermonMedia } from '../hooks/useSermonMedia';
import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import { createStyledPDF, addSection, cleanAIText, cleanAIArray, createSermonPDF } from '../lib/pdf';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType, convertInchesToTwip } from 'docx';
import { createWordDocument } from '../lib/docx';

interface SermonData {
  id: string;
  title: string;
  scripture: string;
  content: string;
  date: string | null;
  status: string;
  user_id: string;
}

interface SharedSermonData {
  id: string;
  sermon_id: string;
  user_id: string;
  created_at: string;
  expires_at: string | null;
  summary: string | null;
  questions: string[] | null;
  youtube_url: string | null;
}

// Helper function for retrying download operations
const retryDownload = async (downloadFn: () => void, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Download timed out'));
        }, 10000); // 10 second timeout

        try {
          downloadFn();
          clearTimeout(timeout);
          resolve(true);
        } catch (error) {
          clearTimeout(timeout);
          reject(error);
        }
      });
      return; // Success, exit retry loop
    } catch (error) {
      console.log(`Download attempt ${i + 1} failed:`, error);
      if (i === maxRetries - 1) {
        throw error; // Last attempt failed
      }
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

// Helper function to parse summary content - handles both plain text and JSON format
const parseSummaryContent = (summary: string): string => {
  if (!summary) return '';
  
  // Check if the summary is JSON (from the new AI format)
  if (summary.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(summary);
      // Extract the bulletin summary from the JSON structure
      if (parsed.bulletinSummary) {
        return parsed.bulletinSummary;
      }
      // Fallback: try to construct a summary from available fields
      if (parsed.mainPoints && Array.isArray(parsed.mainPoints)) {
        return parsed.mainPoints.join('\n\n');
      }
      return summary;
    } catch (e) {
      // Not valid JSON, return as-is
      return summary;
    }
  }
  
  return summary;
};

export function SharedSermon() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sermon, setSermon] = useState<SermonData | null>(null);
  const [sharedData, setSharedData] = useState<SharedSermonData | null>(null);
  const [processedContent, setProcessedContent] = useState<any[]>([]);
  const [showMediaDownloader, setShowMediaDownloader] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [showContent, setShowContent] = useState(false);
  const { loadSectionMedia, downloadAllImages } = useSermonMedia();
  const [sectionMediaCounts, setSectionMediaCounts] = useState<Record<string, number>>({});
  const [wordCount, setWordCount] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingGuides, setGeneratingGuides] = useState(false);
  const [exportLoading, setExportLoading] = useState<'pdf' | 'word' | null>(null);
  const [commentary, setCommentary] = useState<string | null>(null);
  const [applications, setApplications] = useState<any[] | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMenu, setShowMenu] = useState(false);
  const [downloadingAllImages, setDownloadingAllImages] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check for mobile on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchSharedSermon = async () => {
      try {
        setLoading(true);

        // Create an anonymous supabase client to ensure no auth is required
        const { createClient } = await import('@supabase/supabase-js');
        const anonSupabase = createClient(
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY
        );

        // First, get the shared sermon data including study guide URLs
        const { data: sharedSermonData, error: sharedError } = await anonSupabase
          .from('shared_sermons')
          .select('*, small_group_guide_url, personal_study_guide_url')
          .eq('id', id)
          .single();

        if (sharedError) throw sharedError;
        if (!sharedSermonData) throw new Error('Shared sermon not found');

        setSharedData(sharedSermonData);

        // Then, get the actual sermon data using public access
        const { data: sermonData, error: sermonError } = await anonSupabase
          .from('sermons')
          .select('*')
          .eq('id', sharedSermonData.sermon_id)
          .single();

        if (sermonError) throw sermonError;
        if (!sermonData) throw new Error('Sermon not found');

        setSermon(sermonData);

        // Process the content
        const content = processContent(sermonData.content);
        setProcessedContent(content);

        // Calculate word count and reading time
        calculateStats(sermonData.content);

        // Load media counts for each section
        const mediaCounts: Record<string, number> = {};
        for (const element of content) {
          if (element.type === 'section' && element.sectionId) {
            const sectionMedia = await loadSectionMedia(sermonData.id, element.sectionId);
            mediaCounts[element.sectionId] = sectionMedia.length;
          }
        }
        setSectionMediaCounts(mediaCounts);

        // Generate commentary and applications if they don't exist
        if (sharedSermonData.summary && (!commentary || !applications)) {
          generateCommentaryAndApplications(sermonData.content);
        }

      } catch (error) {
        console.error('Error fetching shared sermon:', error);
        setError(error instanceof Error ? error.message : 'Failed to load shared sermon');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSharedSermon();
    }
  }, [id, loadSectionMedia]);

  const calculateStats = (htmlContent: string) => {
    // Create a temporary div to extract text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const text = tempDiv.textContent || tempDiv.innerText || '';

    // Count words
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    setWordCount(words);

    // Calculate reading time (average reading speed: 200 words per minute)
    const minutes = Math.ceil(words / 200);
    setReadingTime(minutes);
  };

  const processContent = (htmlContent: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const elements = Array.from(doc.body.children);

    return elements.map(element => {
      if (element.classList.contains('sermon-section')) {
        const sectionType = Array.from(element.classList)
          .find(cls => cls.startsWith('sermon-section-'))
          ?.replace('sermon-section-', '');

        const pointNumber = element.getAttribute('data-point-number');
        const attribution = element.getAttribute('data-attribution');
        const sectionId = element.getAttribute('data-section-id');

        // Check if this section is hidden in localStorage
        let isVisible = true;
        if (sermon && sectionId) {
          const savedState = localStorage.getItem(`sermon-section-visibility-${sermon.id}-${sectionId}`);
          isVisible = savedState !== null ? JSON.parse(savedState) : true;
        }

        return {
          type: 'section',
          sectionType: sectionType || '',
          pointNumber: pointNumber || '',
          attribution: attribution || '',
          sectionId: sectionId || '',
          content: element.innerHTML,
          className: element.className,
          isVisible
        };
      } else {
        return {
          type: 'content',
          content: element.outerHTML,
          tag: element.tagName.toLowerCase()
        };
      }
    }).filter(element => {
      // Filter out hidden sections
      if (element.type === 'section') {
        return element.isVisible !== false;
      }
      return true;
    });
  };

  const handleShowMediaDownloader = (sectionId: string) => {
    setSelectedSectionId(sectionId);
    setShowMediaDownloader(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return '';
    }
  };

  const handleCopyLink = async () => {
    try {
      // Use the Clipboard API for better mobile compatibility
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';  // Avoid scrolling to bottom
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
        } catch (err) {
          console.error('Fallback: Could not copy text: ', err);
        }

        document.body.removeChild(textArea);
      }

      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Function to generate commentary and applications only if they don't exist
  const generateCommentaryAndApplications = async (content: string) => {
    if (!content || (commentary && applications)) return;

    try {
      setGeneratingGuides(true);

      // Only generate if we don't already have the content
      if (!commentary) {
        // Extract text content from HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent || tempDiv.innerText;

        if (textContent) {
          const maxChars = 10000;
          const truncatedContent = textContent.length > maxChars 
            ? textContent.slice(0, maxChars) + '\n\n[Content truncated for processing]'
            : textContent;

          try {
            console.log('Generating commentary for shared sermon...');

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analysis`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
              },
              body: JSON.stringify({
                type: 'summary',
                content: `Sermon Title: ${sermon?.title || 'Untitled'}
Scripture: ${sermon?.scripture || 'Multiple Passages'}
Content: ${truncatedContent}

Create a warm, pastoral summary of this sermon that captures the heart of the message in an engaging and relational way. Write as if you're sharing the key insights with a friend or congregation member. The summary should:

1. Highlight the main spiritual truths and encouragements from the message
2. Connect the biblical teaching to everyday life and faith journey
3. Use warm, accessible language that invites reflection and application
4. Emphasize God's love, grace, and practical guidance for living
5. Sound conversational and caring rather than academic or formal

Write this as a pastoral summary of about 150-200 words that would encourage someone to engage with the full sermon.`,
                enhancementType: 'pastoral'
              })
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.content) {
                setCommentary(data.content);
              } else {
                throw new Error('No content from AI service');
              }
            } else {
              throw new Error('AI service unavailable');
            }
          } catch (err) {
            console.error('Error generating commentary:', err);
            // Use contextual fallback based on sermon details
            const contextualCommentary = `This sermon on "${sermon?.title || 'God\'s Word'}" from ${sermon?.scripture || 'Scripture'} explores important theological themes that are relevant to our daily Christian walk. The message provides both historical context and practical wisdom for living out our faith today.`;
            setCommentary(contextualCommentary);
          }
        }
      }

      if (!applications) {
        // Generate application points only if we don't have them
        try {
          console.log('Generating application points for shared sermon...');

          const contextualApplications = [
            {
              title: "Daily Scripture Reflection",
              description: `Set aside time each morning to read and meditate on ${sermon?.scripture || 'the key passages'} from this sermon, asking God to reveal how they apply to your current circumstances.`
            },
            {
              title: "Apply Biblical Principles",
              description: "Identify one specific area of your life where you can apply the main teaching from this message this week, and take concrete steps toward biblical obedience."
            },
            {
              title: "Share Gospel Truths",
              description: "Find an opportunity to share one key insight from this sermon with a friend, family member, or colleague, opening the door for spiritual conversation."
            },
            {
              title: "Prayer and Surrender",
              description: "Spend time in prayer asking God to help you understand and live out the truths presented in this message, surrendering areas of resistance or doubt."
            },
            {
              title: "Community Connection",
              description: "Reach out to someone from your church community to discuss this sermon together, encouraging one another in practical faith application."
            }
          ];
          setApplications(contextualApplications);
        } catch (err) {
          console.error('Error generating application points:', err);
        }
      }
    } catch (error) {
      console.error('Error generating commentary and applications:', error);
    } finally {
      setGeneratingGuides(false);
    }
  };



  const handleDownloadSmallGroupGuide = async () => {
    if (!sermon || !sharedData) return;

    try {
      setGeneratingGuides(true);
      
      if (sharedData.summary && sharedData.questions) {
        const blob = createSmallGroupGuidePDF(
          sermon.title,
          sermon.scripture,
          parseSummaryContent(sharedData.summary),
          sharedData.questions
        );
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${sermon.title.toLowerCase().replace(/\s+/g, '-')}-small-group-guide.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading small group guide:', error);
      setError('Failed to download study guide. Please try again.');
    } finally {
      setGeneratingGuides(false);
    }
  };

  const createSmallGroupGuidePDF = (
    title: string,
    scripture: string,
    summary: string,
    questions: string[]
  ): Blob => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    let yPos = createStyledPDF({
      title: 'Small Group Discussion Guide',
      subtitle: title,
      date: sermon?.date ? formatDate(sermon.date) : undefined
    }, doc);

    if (scripture) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(12);
      doc.text(`Scripture: ${scripture}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;
    }

    // Add summary section with colored header
    if (summary) {
      const summaryLines = doc.splitTextToSize(summary, pageWidth - 40);
      const summaryContentHeight = summaryLines.length * 15;
      const totalSummaryHeight = 50 + summaryContentHeight; // Header + content

      // Only add new page if the entire summary section won't fit
      if (yPos + totalSummaryHeight > pageHeight - 40) {
        doc.addPage();
        yPos = 30;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(40, 116, 166); // electric blue

      // Add background box for section title
      const titleBoxHeight = 25;
      doc.setFillColor(240, 240, 240);
      doc.rect(10, yPos - 15, pageWidth - 20, titleBoxHeight, 'F');

      doc.text('Sermon Summary', 20, yPos);
      yPos += 25;

      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);

      doc.text(summaryLines, 20, yPos);
      yPos += summaryContentHeight + 20;
    }

    // Add questions section with colored header
    if (questions && questions.length > 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(45, 127, 94); // emerald

      // Add background box for section title
      const titleBoxHeight = 25;
      doc.setFillColor(240, 240, 240);
      doc.rect(10, yPos - 15, doc.internal.pageSize.width - 20, titleBoxHeight, 'F');

      doc.text('Discussion Questions', 20, yPos);
      yPos += 25;

      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);

      questions.forEach((question, index) => {
        if (yPos > doc.internal.pageSize.height - 80) {
          doc.addPage();
          yPos = 30;
        }

        const questionText = `${index + 1}. ${cleanAIText(question)}`;
        const questionLines = doc.splitTextToSize(questionText, doc.internal.pageSize.width - 40);
        doc.text(questionLines, 20, yPos);
        yPos += (questionLines.length * 15) + 10;
      });

      yPos += 10;
    }

    // Add notes section with colored header
    if (yPos > doc.internal.pageSize.height - 150) {
      doc.addPage();
      yPos = 30;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(217, 164, 65); // gold

    // Add background box for section title
    const titleBoxHeight = 25;
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPos - 15, doc.internal.pageSize.width - 20, titleBoxHeight, 'F');

    doc.text('Notes', 20, yPos);
    yPos += 35;

    // Add lines for notes
    const lineSpacing = 15;
    for (let i = 0; i < 10; i++) {
      if (yPos > doc.internal.pageSize.height - 50) {
        doc.addPage();
        yPos = 30;
      }

      doc.setDrawColor(200, 200, 200);
      doc.line(20, yPos, doc.internal.pageSize.width - 20, yPos);
      yPos += lineSpacing;
    }

    // Add footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('Created with SermonBuild', 20, doc.internal.pageSize.height - 20);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 20);
    }

    return doc.output('blob');
  };

  const createPersonalStudyGuidePDF = (
    title: string,
    scripture: string,
    summary: string,
    commentary: string | null,
    applications: any[]
  ): Blob => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;

    // Create title page
    let yPos = 40;

    // Main title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text('Personal Study Guide', pageWidth / 2, yPos, { align: 'center' });
    yPos += 20;

    // Sermon title
    if (title) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(45, 127, 94); // emerald
      doc.text(title, pageWidth / 2, yPos, { align: 'center' });
      yPos += 20;
    }

    // Scripture reference
    if (scripture) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Scripture: ${scripture}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;
    }

    // Date
    if (sermon?.date) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text(formatDate(sermon.date), pageWidth / 2, yPos, { align: 'center' });
      yPos += 25;
    }

    // Add horizontal line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 20;

    // Add summary section
    if (summary) {
      // Check if we need a new page for the section header and some content
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(45, 127, 94); // emerald
      doc.text('Sermon Summary', margin, yPos);
      yPos += 20;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      const summaryLines = doc.splitTextToSize(summary, pageWidth - 40);

      // Check if summary content fits on current page
      if (yPos + (summaryLines.length * 15) > pageHeight - 50) {
        doc.addPage();
        yPos = margin;
      }

      doc.text(summaryLines, margin, yPos);
      yPos += (summaryLines.length * 15) + 20;
    }

    // Add commentary section
    if (commentary) {
      // Check if we need a new page for the section header and some content
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(178, 42, 47); // crimson
      doc.text('Biblical Commentary', margin, yPos);
      yPos += 20;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      const commentaryLines = doc.splitTextToSize(commentary, pageWidth - 40);

      // Check if commentary content fits on current page
      if (yPos + (commentaryLines.length * 15) > pageHeight - 50) {
        doc.addPage();
        yPos = margin;
      }

      doc.text(commentaryLines, margin, yPos);
      yPos += (commentaryLines.length * 15) + 20;
    }

    // Add application points
    if (applications && applications.length > 0) {
      // Check if we need a new page for the section header and some content
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = margin;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(217, 164, 65); // gold

      const titleBoxHeight = 20;
      doc.setFillColor(255, 252, 240);
      doc.rect(margin - 5, yPos - 12, pageWidth - (margin * 2) + 10, titleBoxHeight, 'F');

      doc.text('Application Points', margin, yPos);
      yPos += 30;

      applications.forEach((app, index) => {
        const estimatedHeight = 60;
        if (yPos + estimatedHeight > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(217, 164, 65); // gold
        doc.text(`${index + 1}. ${app.title}`, margin + 5, yPos);
        yPos += 18;

        if (app.description) {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(11);
          doc.setTextColor(0, 0, 0);

          const descLines = doc.splitTextToSize(app.description, pageWidth - (margin * 2) - 10);

          if (yPos + (descLines.length * 14) > pageHeight - 40) {
            doc.addPage();
            yPos = margin;
          }

          doc.text(descLines, margin + 10, yPos);
          yPos += (descLines.length * 14) + 15;
        }
      });
    }

    // Add reflection section
    // Check if we need a new page for the reflection section
    if (yPos > pageHeight - 200) { // Need space for header + reflection lines
      doc.addPage();
      yPos = margin;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(45, 127, 94); // emerald

    const titleBoxHeight = 25;
    doc.setFillColor(240, 240, 240);
    doc.rect(10, yPos - 15, doc.internal.pageSize.width - 20, titleBoxHeight, 'F');

    doc.text('Personal Reflection', 20, yPos);
    yPos += 35;

    // Add lines for reflection
    const lineSpacing = 15;
    for (let i = 0; i < 10; i++) {
      if (yPos > doc.internal.pageSize.height - 50) {
        doc.addPage();
        yPos = 30;
      }

      doc.setDrawColor(200, 200, 200);
      doc.line(20, yPos, doc.internal.pageSize.width - 20, yPos);
      yPos += lineSpacing;
    }

    // Add footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(150, 150, 150);
      doc.text('Created with SermonBuild', 20, doc.internal.pageSize.height - 20);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 20);
    }

    return doc.output('blob');
  };

  const handleDownloadStudyGuide = async () => {
    if (!sermon || !sharedData) return;

    try {
      setGeneratingGuides(true);
      
      if (sharedData.summary) {
        if (!commentary || !applications) {
          await generateCommentaryAndApplications(sermon.content);
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        const blob = createPersonalStudyGuidePDF(
          sermon.title,
          sermon.scripture,
          parseSummaryContent(sharedData.summary),
          commentary || "This sermon explores key theological themes that resonate throughout Scripture. The passage highlights God's sovereignty and grace, reminding us of His faithfulness throughout history.",
          applications || []
        );
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${sermon.title.toLowerCase().replace(/\s+/g, '-')}-personal-study-guide.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading personal study guide:', error);
      setError('Failed to download study guide. Please try again.');
    } finally {
      setGeneratingGuides(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!sermon) return;

    try {
      setExportLoading('pdf');
      setError(null);

      // Filter out hidden sections for PDF export
      const visibleContent = processedContent.filter(element => {
        if (element.type === 'section') {
          return element.isVisible !== false;
        }
        return true;
      });
      
      // Use the same PDF generation function as preach mode for consistency
      const blob = createSermonPDF(sermon, visibleContent);

      // Create download link with retry logic
      const url = URL.createObjectURL(blob);
      
      await retryDownload(() => {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sermon.title.toLowerCase().replace(/\s+/g, '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please check your internet connection and try again.');
    } finally {
      setExportLoading(null);
    }
  };

  const handleDownloadWord = async () => {
    if (!sermon) return;

    try {
      setExportLoading('word');
      setError(null);

      // Filter out hidden sections for Word export
      const visibleContent = processedContent.filter(element => {
        if (element.type === 'section') {
          return element.isVisible !== false;
        }
        return true;
      });
      
      // Use the same createWordDocument function as in preach mode
      const blob = await createWordDocument(sermon, visibleContent);

      // Save the file using file-saver with retry logic
      await retryDownload(() => {
        saveAs(blob, `${sermon.title.toLowerCase().replace(/\s+/g, '-')}.docx`);
      });
    } catch (error) {
      console.error('Error generating Word document:', error);
      setError('Failed to generate Word document. Please check your internet connection and try again.');
    } finally {
      setExportLoading(null);
    }
  };

  const handleDownloadAllImages = async () => {
    if (!sermon) return;

    try {
      setDownloadingAllImages(true);
      setError(null);

      const result = await downloadAllImages(sermon.id, sermon.title);
      
      if (result.downloadedCount < result.totalCount) {
        setError(`Downloaded ${result.downloadedCount} of ${result.totalCount} images. Some files may have been unavailable.`);
      }
    } catch (error) {
      console.error('Error downloading all images:', error);
      setError(error instanceof Error ? error.message : 'Failed to download images. Please try again.');
    } finally {
      setDownloadingAllImages(false);
    }
  };

  // Helper function to extract paragraphs from HTML content
  function extractParagraphs(htmlContent: string): Paragraph[] {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Get all text nodes and paragraphs
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Split into paragraphs
    const paragraphs = textContent.split(/\n+/).filter(Boolean);

    // Convert to Word paragraphs
    return paragraphs.map(paragraph => {
      // Check if it's a bullet point
      const isBulletPoint = paragraph.trim().startsWith('•') || 
                            paragraph.trim().startsWith('-') ||
                            paragraph.trim().startsWith('*');

      if (isBulletPoint) {
        return new Paragraph({
          text: paragraph.trim().replace(/^[•\-*]\s*/, ''),
          bullet: {
            level: 0
          },
          spacing: {
            after: 120, // 6pt spacing after
          },
        });
      }

      return new Paragraph({
        text: paragraph.trim(),
        spacing: {
          after: 240, // 12pt spacing after
        },
      });
    });
  }

  // Helper function to get section color for Word document
  function getSectionColor(sectionType: string): string {
    switch (sectionType) {
      case 'scripture':
        return "B22A2F"; // crimson
      case 'illustration':
        return "2D7F5E"; // emerald
      case 'quote':
        return "2874A6"; // electric
      case 'application':
        return "D9A441"; // gold
      case 'main-point':
        return "E86A33"; // fiery
      default:
        return "4F5D75"; // slate
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-sand/10 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (error || !sermon || !sharedData) {
    return (
      <div className="min-h-screen bg-sand/10 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-8">
            <div className="flex items-center text-crimson mb-4">
              <AlertCircle className="h-6 w-6 mr-2" />
              <h2 className="text-xl font-semibold">Error Loading Sermon</h2>
            </div>
            <p className="text-slate mb-6">{error || 'The shared sermon could not be found or has expired.'}</p>
            <Link to="/" className="text-electric hover:text-electric/80">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const sectionIcons = {
    'scripture': Book,
    'illustration': Image,
    'quote': Quote,
    'application': Target,
    'main-point': Star,
  };

  const metaDescription = (() => {
    const summary = sharedData.summary ? parseSummaryContent(sharedData.summary) : '';
    const base = sermon.scripture ? `${sermon.scripture} — ` : '';
    const text = summary || `A sermon by ${sermon.title}.`;
    return (base + text).substring(0, 155);
  })();

  return (
    <div className="min-h-screen bg-sand/5 pb-16 md:pb-0">
      <Helmet>
        <title>{sermon.title} — SermonBuild</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${sermon.title} — SermonBuild`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${sermon.title} — SermonBuild`} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-midnight">{sermon.title}</h1>
          <div className="mt-2 text-lg md:text-xl text-slate">{sermon.scripture}</div>
          <div className="mt-2 text-sm text-slate">{formatDate(sermon.date)}</div>
          <div className="mt-1 text-sm text-slate">{wordCount} words · {readingTime} min read</div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={handleCopyLink}
            className="flex items-center px-3 py-1.5 md:px-4 md:py-2 bg-electric text-white rounded-lg hover:bg-electric/90 text-sm"
          >
            <LinkIcon className="h-4 w-4 mr-1 md:mr-2" />
            {linkCopied ? 'Link Copied!' : 'Share'}
          </button>

          {/* Desktop actions */}
          <div className="hidden md:flex gap-2">
            <button
              onClick={handleDownloadPDF}
              disabled={exportLoading === 'pdf'}
              className="flex items-center px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
            >
              {exportLoading === 'pdf' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating PDF...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  PDF
                </>
              )}
            </button>

            <button
              onClick={handleDownloadWord}
              disabled={exportLoading === 'word'}
              className="flex items-center px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
            >
              {exportLoading === 'word' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Word...
                </>
              ) : (
                <>
                  <FileWord className="h-4 w-4 mr-2" />
                  Word
                </>
              )}
            </button>

            <button
              onClick={handleDownloadAllImages}
              disabled={downloadingAllImages}
              className="flex items-center px-4 py-2 bg-emerald text-white rounded-lg hover:bg-emerald/90 disabled:opacity-50"
            >
              {downloadingAllImages ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  All Images
                </>
              )}
            </button>

            <button
              className="flex items-center px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>

          {/* Mobile menu */}
          <div className="relative md:hidden" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center px-3 py-1.5 bg-electric text-white rounded-lg hover:bg-electric/90 text-sm"
            >
              <MoreVertical className="h-4 w-4 mr-1" />
              Options
            </button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-sand/20 z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      handleDownloadPDF();
                      setShowMenu(false);
                    }}
                    disabled={exportLoading === 'pdf'}
                    className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                  >
                    {exportLoading === 'pdf' ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating PDF...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      handleDownloadWord();
                      setShowMenu(false);
                    }}
                    disabled={exportLoading === 'word'}
                    className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                  >
                    {exportLoading === 'word' ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Word...
                      </>
                    ) : (
                      <>
                        <FileWord className="h-4 w-4 mr-2" />
                        Download Word
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      handleDownloadAllImages();
                      setShowMenu(false);
                    }}
                    disabled={downloadingAllImages}
                    className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                  >
                    {downloadingAllImages ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Download All Images
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      window.print();
                      setShowMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center text-red-700">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p className="text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* YouTube video */}
        {sharedData.youtube_url && (
          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-midnight mb-4">Sermon Video</h2>
            <div className="relative w-full bg-black rounded-lg overflow-hidden shadow-md" style={{ paddingBottom: '56.25%' /* 16:9 aspect ratio */ }}>
              <ReactPlayer
                url={sharedData.youtube_url}
                width="100%"
                height="100%"
                controls={true}
                playing={false}
                light={false}
                pip={true}
                stopOnUnmount={false}
                style={{ position: 'absolute', top: 0, left: 0 }}
                config={{
                  youtube: {
                    playerVars: {
                      autoplay: 0,
                      controls: 1,
                      disablekb: 0,
                      enablejsapi: 1,
                      fs: 1,
                      modestbranding: 1,
                      rel: 0,
                      showinfo: 1
                    }
                  }
                }}
                onError={(error) => {
                  console.error('ReactPlayer error:', error);
                }}
                onReady={() => {
                  console.log('ReactPlayer ready');
                }}
              />
            </div>
          </div>
        )}

        {/* Sermon Summary */}
        <div className="mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-midnight mb-4">Sermon Summary</h2>
          <div className="prose prose-lg max-w-none prose-p:text-slate prose-headings:text-midnight prose-strong:text-midnight">
            {sharedData.summary ? (
              <ReactMarkdown>{parseSummaryContent(sharedData.summary)}</ReactMarkdown>
            ) : (
              <p className="text-slate italic">No summary available.</p>
            )}
          </div>
        </div>

        {/* Sermon Content */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-midnight">Sermon Content</h2>
            <button
              onClick={() => setShowContent(!showContent)}
              className="flex items-center text-electric hover:text-electric/80"
            >
              {showContent ? (
                <>
                  <MinusCircle className="h-4 w-4 mr-1" />
                  Hide Content
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Show Content
                </>
              )}
            </button>
          </div>

          {!showContent ? (
            <div className="text-center py-12 bg-sand/10 rounded-lg">
              <p className="text-slate">The full sermon content is available for download or viewing.</p>
              <div className="mt-4 flex justify-center space-x-4">
                <button
                  onClick={() => setShowContent(true)}
                  className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
                >
                  View Content
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="px-4 py-2 bg-sand/20 text-slate rounded-lg hover:bg-sand/30"
                >
                  Download PDF
                </button>
              </div>
            </div>
          ) : (
            <div className="sermon-content-display max-w-none">
              {processedContent.map((element, index) => {
                if (element.type === 'section') {
                  // Skip rendering if section is not visible
                  if (element.isVisible === false) {
                    return null;
                  }

                  const sectionType = element.sectionType;
                  const Icon = sectionIcons[sectionType as keyof typeof sectionIcons] || Book;

                  return (
                    <div
                      key={index}
                      className="my-8 rounded-lg overflow-hidden border-l-4"
                      style={{
                        backgroundColor:
                          sectionType === 'scripture' ? 'var(--crimson-bg)' :
                          sectionType === 'illustration' ? 'var(--emerald-bg)' :
                          sectionType === 'quote' ? 'var(--electric-bg)' :
                          sectionType === 'application' ? 'var(--gold-bg)' :
                          sectionType === 'main-point' ? 'var(--fiery-bg)' :
                          'var(--slate-bg)',
                        borderColor:
                          sectionType === 'scripture' ? 'var(--crimson)' :
                          sectionType === 'illustration' ? 'var(--emerald)' :
                          sectionType === 'quote' ? 'var(--electric)' :
                          sectionType === 'application' ? 'var(--gold)' :
                          sectionType === 'main-point' ? 'var(--fiery)' :
                          'var(--slate)'
                      }}
                    >
                      <div className="flex items-center justify-between gap-2 px-6 pt-6 pb-2 text-midnight">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-medium uppercase">
                            {sectionType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>
                          {element.pointNumber && sectionType === 'main-point' && (
                            <span className="ml-2 px-2 py-0.5 bg-sand/30 rounded text-sm font-bold">
                              {element.pointNumber}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          {element.attribution && sectionType === 'quote' && (
                            <span className="italic text-sm opacity-80">
                              — {element.attribution}
                            </span>
                          )}
                          {element.sectionId && sectionMediaCounts[element.sectionId] > 0 && (
                            <button
                              onClick={() => handleShowMediaDownloader(element.sectionId)}
                              className="ml-2 p-1 rounded bg-sand/20 hover:bg-sand/30 relative"
                              title="Download Media"
                            >
                              <Download className="h-4 w-4 text-slate" />
                              <span className="absolute -top-1 -right-1 bg-crimson text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                {sectionMediaCounts[element.sectionId]}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div
                        className="px-6 pb-6 sermon-content-display"
                        dangerouslySetInnerHTML={{ __html: sanitizeSermonHtml(element.content) }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className="sermon-content-display"
                      dangerouslySetInnerHTML={{ __html: sanitizeSermonHtml(element.content) }}
                    />
                  );
                }
              })}
            </div>
          )}
        </div>

        {/* Discussion Questions */}
        {sharedData.questions && sharedData.questions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-midnight mb-4">Discussion Questions</h2>
            <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
              <ol className="list-decimal pl-5 space-y-3">
                {sharedData.questions.map((question, index) => (
                  <li key={index} className="text-slate">{question}</li>
                ))}
              </ol>
            </div>
          </div>
        )}

        {/* Additional Resources */}
        <div className="mt-12">
          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-midnight">Study Guides</h2>
            <p className="text-slate mt-2">Download comprehensive study guides with discussion questions and application points.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Small Group Guide */}
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-start mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FileText className="h-6 w-6 text-electric" />
                </div>
                <div>
                  <h4 className="font-medium text-midnight">Small Group Discussion Guide</h4>
                  <p className="text-sm text-slate mt-1">
                    Includes sermon summary, discussion questions, and space for notes.
                  </p>
                </div>
              </div>
              <button
                onClick={handleDownloadSmallGroupGuide}
                disabled={generatingGuides}
                className="w-full px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center justify-center disabled:opacity-50"
              >
                {generatingGuides ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Guide
                  </>
                )}
              </button>
            </div>

            {/* Personal Study Guide */}
            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-start mb-4">
                <div className="bg-green-100 p-2 rounded-lg mr-3">
                  <BookOpen className="h-6 w-6 text-emerald" />
                </div>
                <div>
                  <h4 className="font-medium text-midnight">Personal Study Guide</h4>
                  <p className="text-sm text-slate mt-1">
                    Includes commentary, application points, and reflection space.
                  </p>
                </div>
              </div>
              <button
                onClick={handleDownloadStudyGuide}
                disabled={generatingGuides}
                className="w-full px-4 py-2 bg-emerald text-white rounded-lg hover:bg-emerald/90 flex items-center justify-center disabled:opacity-50"
              >
                {generatingGuides ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Guide
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t border-sand/20 mt-8 py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-slate mb-4 md:mb-0">
              Shared via SermonBuild
            </div>
            <Link
              to="/"
              className="text-electric hover:text-electric/80 text-sm"
            >
              Create your own sermons with SermonBuild
            </Link>
          </div>
        </div>
      </footer>

      {showMediaDownloader && selectedSectionId && (
        <MediaDownloader
          sermonId={sermon.id}
          sectionId={selectedSectionId}
          onClose={() => setShowMediaDownloader(false)}
        />
      )}
    </div>
  );
}